import { lazy, Suspense, useContext, useEffect } from "react";
import { createBrowserRouter, createRoutesFromElements, Navigate, Outlet, Route, useLocation, useParams } from "react-router-dom";
import { ReactFlowProvider } from "reactflow";
import { AppContext } from "../context/AppContext";
import { isMessengerServiceName } from "../utils/fieldMappingServiceUtils";
import SomethingWentWrong from "./SomethingWentWrong";
import { MODULE_ACCESS_KEY } from "./utils/commonUtils";

function LazyRouteFallbackLoading({ loadingContextValue }) {
  useEffect(() => {
    loadingContextValue.setLoading?.(true);
    return () => {
      if(loadingContextValue?.isAppContextCreated) loadingContextValue?.setLoading?.(false);
    };
  }, []);
  return null;
}
// Lazy load route components for code splitting
const LoginAppsPage = lazy(() => import("../login/LoginAppsPage"));
const CallFlow = lazy(() => import("../voice/components/CallFlow"));
const AppLayout = lazy(() => import("./AppLayout"));
const NoAccessToExternalPage = lazy(() => import("../voice/components/info-component/NoAccessToExternalPage"));
const CallHandlingFlowChart = lazy(() => import("../voice/components/CallHandlingFlowChart"));
const Dialpad = lazy(() => import("../voice/components/Dialpad"));
const VoiceCallLog = lazy(() => import("../voice/components/VoiceCallLog"));
const ShowAutomationComponentForm = lazy(() => import("./../pages/automation/ShowAutomationComponentForm"));
const ZohoCRMLandingPage = lazy(() => import("./../pages/services/ZohoCRMLandingPage"));
const TeamsLandingPage = lazy(() => import("./../pages/services/TeamsLandingPage"));
const AutomatedMessages = lazy(() => import("./AutomatedMessages"));
const ApiKeysSettings = lazy(() => import("./ApiKeysSettings"));
const BlockedNumbers = lazy(() => import("./BlockedNumbers"));
const CampaignAnalytics = lazy(() => import("./CampaignAnalytics"));
const ConversationWrapper = lazy(() => import("./ConversationWrapper"));
const Home = lazy(() => import("./Home"));
const MessageLogs = lazy(() => import("./MessageLogs"));
const TenDlc = lazy(() => import("./OnboardingComponents/10DLC/TenDlc"));
const ActiveNumbers = lazy(() => import("./OnboardingComponents/ActiveNumbers"));
const ChannelsConfigPage = lazy(() => import("./OnboardingComponents/ChannelsConfiguration"));
const GeneralSettings = lazy(() => import("./Setting/GeneralAppsSettings"));
const FieldMappingsSettings = lazy(() => import("./Setting/FieldMappingsSettings"));
const Settings = lazy(() => import("./Setting/Settings"));
const Templates = lazy(() => import("./Templates"));
const Users = lazy(() => import("./Users"));
const AutomationMessageLogs = lazy(() => import("./automation/AutomationMessageLogs"));
const Campaign = lazy(() => import("./campaign/components/campaign/Campaign"));
const CampaignOverview = lazy(() => import("./campaign/components/overview/CampaignOverview"));
const SendSMS = lazy(() => import("./outbound/SendSMS"));
const TextSMS = lazy(() => import("./services/TextSMS"));
const TextSMSAuthPage = lazy(() => import("./services/TextSMSAuthPage"));
const Teams = lazy(() => import("./Teams"));
const NoAccess = lazy(() => import("../pages/noAccess"));
const PermissionRoute = lazy(() => import("../components/PermissionRoute"));
const BackToChannelsButton = lazy(() =>
  import("./services/TextSMSAuthPage").then((mod) => ({ default: mod.BackToChannelsButton }))
);
const EmailLoginComponent = lazy(() => import("./Login/EmailLoginComponent"));
const LogoutPage = lazy(() => import("./LogoutPage"));
const AccountDeletedPage = lazy(() => import("./AccountDeletedPage"));
const Contacts = lazy(() => import("./contacts/Contacts"));
const DripCampaign = lazy(() => import("./DripCampaign"));
const AIDataSources = lazy(() => import("./ai/DataSources"));
const Playground = lazy(() => import("./ai/Playground"));
const GetStarted = lazy(() => import("./GetStarted"));
const AnalyticsDashboard = lazy(() => import("./analytics/AnalyticsDashboard"));
const TriggerRules = lazy(() => import("./automation/TriggerRules.tsx"));

const ChannelsGuardRoute = () => {
  const [appProperties] = useContext(AppContext);
  const location = useLocation();
  const { serviceName } = useParams();
  const shouldShowMessengerChannels = appProperties?.controller?.showMessengerChannels !== false;

  const isMessengerChannel = isMessengerServiceName(serviceName);

  if (!shouldShowMessengerChannels && isMessengerChannel) {
    return <Navigate to={`/settings/channels${location.search}`} replace />;
  }

  return (
    <PermissionRoute module="channels">
      <TextSMSAuthPage />
    </PermissionRoute>
  );
};

const ChannelRedirect = ({ loadingContextValue }) => {
  const { serviceName } = useParams();
  if (serviceName === "helloSend") {
    return (
      <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
        <PermissionRoute module="channels">
            <ActiveNumbers backToChannelsButton={
              <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
                <BackToChannelsButton />
              </Suspense>
            } />
        </PermissionRoute>
      </Suspense>
    );
  }
  return <Navigate to={`/settings/channels/${serviceName}${window.location.search}`} replace />;
};

const createRouter = (loadingContextValue, locationState, accessDenied, setAccessDenied) => {
  const { editNodes, editEdges } = locationState || { editNodes: [], editEdges: [] };
  const { setLoading, loading } = loadingContextValue;
  return createBrowserRouter(
    createRoutesFromElements(
      <Route element={<Outlet />} errorElement={<SomethingWentWrong />}>
      <Route path="no-permission" element={<Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}><NoAccessToExternalPage setLoading={setLoading} /></Suspense>} />
      <Route
        path="/"
        element={
          <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
            <PermissionRoute module={"external page"}>
              <AppLayout setLoading={setLoading} accessDenied={accessDenied} setAccessDenied={setAccessDenied} />
            </PermissionRoute>
          </Suspense>
        }
      >
        <Route
          path="/channels"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="channels">
                <ChannelsConfigPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          exact
          path="/home"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Home />
            </Suspense>
          }
        />
        <Route
          path="/voice/callflow/createflow"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/settings/callFlow/edit/:callFlowId/phoneNumber/:phoneNumber"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart editNodes={editNodes} editEdges={editEdges} />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/voice/callFlow/edit/:callFlowId/phoneNumber/:phoneNumber"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ReactFlowProvider>
                <CallHandlingFlowChart editNodes={editNodes} editEdges={editEdges} />
              </ReactFlowProvider>
            </Suspense>
          }
        />
        <Route
          path="/voice/dialpad"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Dialpad showDialPad={true} />
            </Suspense>
          }
        />
        <Route
          path="/voice/history"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <VoiceCallLog />
            </Suspense>
          }
        />

        <Route
          path="/voice/callFlow/list"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <CallFlow />
            </Suspense>
          }
        />
        <Route
          path="/settings/number/twilio_voice"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TextSMSAuthPage />
            </Suspense>
          }
        />
        <Route
          path="/settings/twilio"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TextSMSAuthPage />
            </Suspense>
          }
        />
        <Route
          exact
          path="/settings"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Settings />
            </Suspense>
          }
        />
        <Route
          exact
          path="/settings/general"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <GeneralSettings />
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/:serviceName"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ChannelsGuardRoute />
            </Suspense>
          }
        />
        <Route
          path="/settings/teams"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Teams />
            </Suspense>
          }
        />
        <Route
          path="/settings/number/twilio_voice"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TextSMSAuthPage />
            </Suspense>
          }
        />
        <Route
          path="/campaign"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Campaign />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <CampaignOverview />
            </Suspense>
          }
        />
        {/* <Route path="/channels" element={<ChannelsConfigPage />} /> */}
        <Route
          path="/settings/channels"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="channels">
                <ChannelsConfigPage />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/channels/textsms"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="channels">
                <TextSMS />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/textsms"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="channels">
                <TextSMS />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/channels/textsms/:serviceName"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ChannelRedirect loadingContextValue={loadingContextValue} />
            </Suspense>
          }
        />
        <Route
          path="/settings/channels/textsms/:serviceName"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ChannelRedirect loadingContextValue={loadingContextValue} />
            </Suspense>
          }
        />
        <Route
          path="/inbox"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ConversationWrapper />
            </Suspense>
          }
        />
        <Route
          path="/inbox/:contactId"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ConversationWrapper />
            </Suspense>
          }
        />
        <Route path="*" element={<p>There's nothing here: 404!</p>} />
        <Route
          path="/sendsms"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <SendSMS bulk={false} appLoading={loading} setLoading={setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/messages"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <SendSMS bulk={true} setLoading={setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/settings/templates"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Templates />
            </Suspense>
          }
        />
        <Route
          path="/templates"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Templates />
            </Suspense>
          }
        />
        <Route
          path="/settings/users"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="users">
                <Users />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/users"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Users />
            </Suspense>
          }
        />
        <Route
          path="/settings/blockedNumbers"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <BlockedNumbers />
            </Suspense>
          }
        />
        <Route
          path="/settings/api-keys"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ApiKeysSettings />
            </Suspense>
          }
        />
        <Route
          path="/settings/field-mappings"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading loadingContextValue={loadingContextValue} />}>
              <FieldMappingsSettings />
            </Suspense>
          }
        />
        {/* <Route path="/automation" element={<WorkFlow />} /> */}
        <Route
          path="/messaging/automation/trigger-rules"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TriggerRules />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation/trigger-rules/:ruleId"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TriggerRules />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <AutomatedMessages />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation/:eventId/logs"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <AutomationMessageLogs />
            </Suspense>
          }
        />
        <Route
          path="/messaging/automation/:eventId"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <DripCampaign />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list/:campaignId/reports"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <CampaignAnalytics currentItem={"error"} />
            </Suspense>
          }
        />
        <Route
          path="/messaging/campaigns/list/:campaignId/overview"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <CampaignAnalytics currentItem={"overview"} />
            </Suspense>
          }
        />
        <Route path="/messaging/campaigns" element={<Navigate to={`/messaging/campaigns/list${window.location.search}`} />} />
        <Route
          path="/channels/textsms/:serviceName/10DLC"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TenDlc />
            </Suspense>
          }
        />
        <Route
          path="/messaging/logs"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <MessageLogs />
            </Suspense>
          }
        />
        <Route path="/messaging/message-logs" element={<Navigate to={`/messaging/logs${window.location.search}`} />} />
        <Route
          path="/teams/landing"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <TeamsLandingPage />
            </Suspense>
          }
        />
        <Route
          path="/:leftService/landing"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ZohoCRMLandingPage />
            </Suspense>
          }
        />
        <Route
          path="/login"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <EmailLoginComponent />
            </Suspense>
          }
        />
        <Route
          path="/web/signin"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <EmailLoginComponent websiteLogin={true} context={"web_signin"} />
            </Suspense>
          }
        />
        <Route
          path="/web/signup"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <EmailLoginComponent websiteLogin={true} context={"web_signup"} />
            </Suspense>
          }
        />
        <Route
          path="/web/apps"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <LoginAppsPage />
            </Suspense>
          }
        />
        <Route
          path="/logout"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <LogoutPage setLoading={setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/account-deleted"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <AccountDeletedPage setLoading={setLoading} />
            </Suspense>
          }
        />
        <Route
          path="/zoho-crm/webhook"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <ShowAutomationComponentForm source={"custom-action"} />
            </Suspense>
          }
        />
        {/* <Route path="*" element={<h1>404!</h1>} /> */}
        <Route
          path="no-access"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <NoAccess />
            </Suspense>
          }
        />
        <Route
          path="/channels/:serviceName"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="channels">
                <ChannelRedirect loadingContextValue={loadingContextValue} />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/contacts/:recordId"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Contacts />
            </Suspense>
          }
        />
        <Route
          path="/contacts"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <Contacts />
            </Suspense>
          }
        />
        <Route
          path="/get-started"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <GetStarted />
            </Suspense>
          }
        />
        <Route
          path="/ai/data-sources"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="ai">
              <AIDataSources />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/ai/playground"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading  loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module="ai">
                <Playground />
              </PermissionRoute>
            </Suspense>
          }
        />
        <Route
          path="/analytics/*"
          element={
            <Suspense fallback={<LazyRouteFallbackLoading loadingContextValue={loadingContextValue} />}>
              <PermissionRoute module={MODULE_ACCESS_KEY.ANALYTICS}>
                <AnalyticsDashboard />
              </PermissionRoute>
            </Suspense>
          }
        />
      </Route>
      </Route>
    )
  );
};
export default createRouter;
